import _toConsumableArray from "/usr/src/app/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/toConsumableArray";
import _objectSpread from "/usr/src/app/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/objectSpread";
import React from 'react';
import { measureText, getContainingView, getContainingTrack } from '@jbrowse/core/util';
import { getConf } from '@jbrowse/core/configuration';
import { BaseLinearDisplayComponent } from '@jbrowse/plugin-linear-genome-view';
import { observer } from 'mobx-react';
import YScaleBar from '../../shared/YScaleBar';
var trackLabelFontSize = 12.8;
function getOffset(model) {
  var prefersOffset = model.prefersOffset;
  var _getContainingView = getContainingView(model),
    trackLabels = _getContainingView.trackLabels;
  var track = getContainingTrack(model);
  var trackName = getConf(track, 'name');
  return trackLabels === 'overlapping' && !prefersOffset ? measureText(trackName, trackLabelFontSize) + 100 : 10;
}
var Wrapper = observer(function (_ref) {
  var children = _ref.children,
    model = _ref.model,
    exportSVG = _ref.exportSVG;
  if (exportSVG) {
    return React.createElement(React.Fragment, null, children);
  } else {
    var height = model.height;
    return React.createElement("svg", {
      style: {
        position: 'absolute',
        top: 0,
        left: 0,
        pointerEvents: 'none',
        height: height,
        width: getContainingView(model).width
      }
    }, children);
  }
});
var RectBg = function RectBg(props) {
  var _props$color = props.color,
    color = _props$color === void 0 ? 'rgb(255,255,255,0.8)' : _props$color;
  return React.createElement("rect", _objectSpread({}, props, {
    fill: color
  }));
};
var ScoreLegend = observer(function (_ref2) {
  var model = _ref2.model;
  var ticks = model.ticks,
    scaleType = model.scaleType;
  var _getContainingView2 = getContainingView(model),
    width = _getContainingView2.width;
  var legend = "[".concat(ticks === null || ticks === void 0 ? void 0 : ticks.values[0], "-").concat(ticks === null || ticks === void 0 ? void 0 : ticks.values[1], "]") + (scaleType === 'log' ? ' (log scale)' : '');
  var len = measureText(legend, 14);
  var padding = 25;
  var xpos = width - len - padding;
  return React.createElement(React.Fragment, null, React.createElement(RectBg, {
    y: 0,
    x: xpos,
    width: len + 6,
    height: 16
  }), React.createElement("text", {
    y: 13,
    x: xpos
  }, legend));
});
var ColorLegend = observer(function (_ref3) {
  var model = _ref3.model,
    rowHeight = _ref3.rowHeight,
    labelWidth = _ref3.labelWidth,
    exportSVG = _ref3.exportSVG;
  var needsCustomLegend = model.needsCustomLegend,
    needsScalebar = model.needsScalebar,
    needsFullHeightScalebar = model.needsFullHeightScalebar,
    rowHeightTooSmallForScalebar = model.rowHeightTooSmallForScalebar,
    renderColorBoxes = model.renderColorBoxes,
    sources = model.sources;
  var svgFontSize = Math.min(rowHeight, 12);
  var canDisplayLabel = rowHeight > 11;
  var colorBoxWidth = renderColorBoxes ? 15 : 0;
  var legendWidth = labelWidth + colorBoxWidth + 5;
  var svgOffset = exportSVG ? 10 : 0;
  var extraOffset = svgOffset || (needsScalebar && !rowHeightTooSmallForScalebar ? 50 : 0);
  return sources ? React.createElement(React.Fragment, null, /* 0.25 for hanging letters like g */
  needsFullHeightScalebar ? React.createElement(RectBg, {
    y: 0,
    x: extraOffset,
    width: legendWidth,
    height: (sources.length + 0.25) * rowHeight
  }) : null, sources.map(function (source, idx) {
    var boxHeight = Math.min(20, rowHeight);
    return React.createElement(React.Fragment, {
      key: source.name + '-' + idx
    }, !needsFullHeightScalebar ? React.createElement(RectBg, {
      y: idx * rowHeight + 1,
      x: extraOffset,
      width: legendWidth,
      height: boxHeight
    }) : null, source.color ? React.createElement(RectBg, {
      y: idx * rowHeight + 1,
      x: extraOffset,
      width: colorBoxWidth,
      height: needsCustomLegend ? rowHeight : boxHeight,
      color: source.color
    }) : null, canDisplayLabel ? React.createElement("text", {
      y: idx * rowHeight + 13,
      x: extraOffset + colorBoxWidth + 2,
      fontSize: svgFontSize
    }, source.name) : null);
  })) : null;
});
export var StatBars = observer(function (props) {
  var model = props.model,
    orientation = props.orientation,
    exportSVG = props.exportSVG;
  var stats = model.stats,
    needsCustomLegend = model.needsCustomLegend,
    needsFullHeightScalebar = model.needsFullHeightScalebar,
    rowHeightTooSmallForScalebar = model.rowHeightTooSmallForScalebar,
    rowHeight = model.rowHeight,
    sources = model.sources,
    ticks = model.ticks;
  var svgFontSize = Math.min(rowHeight, 12);
  var canDisplayLabel = rowHeight > 11;
  var _getContainingView3 = getContainingView(model),
    viewWidth = _getContainingView3.width;
  var minWidth = 20;
  var ready = stats && sources;
  if (!ready) {
    return null;
  }
  var labelWidth = Math.max.apply(Math, _toConsumableArray(sources.map(function (s) {
    return measureText(s.name, svgFontSize);
  }).map(function (width) {
    return canDisplayLabel ? width : minWidth;
  }) || [0]));
  return React.createElement(Wrapper, _objectSpread({}, props), needsFullHeightScalebar ? React.createElement(React.Fragment, null, React.createElement("g", {
    transform: "translate(".concat(!exportSVG ? getOffset(model) : 0, ",0)")
  }, React.createElement(YScaleBar, {
    model: model,
    orientation: orientation
  })), React.createElement("g", {
    transform: "translate(".concat(viewWidth - labelWidth - 100, ",0)")
  }, React.createElement(ColorLegend, {
    exportSVG: exportSVG,
    model: model,
    rowHeight: 12,
    labelWidth: labelWidth
  }))) : React.createElement(React.Fragment, null, React.createElement(ColorLegend, {
    exportSVG: exportSVG,
    model: model,
    rowHeight: model.rowHeight,
    labelWidth: labelWidth
  }), rowHeightTooSmallForScalebar || needsCustomLegend ? React.createElement(ScoreLegend, _objectSpread({}, props)) : sources.map(function (_source, idx) {
    return React.createElement("g", {
      transform: "translate(0 ".concat(rowHeight * idx, ")"),
      key: JSON.stringify(ticks) + '-' + idx
    }, React.createElement(YScaleBar, {
      model: model,
      orientation: orientation
    }));
  })));
});
export default observer(function (props) {
  var model = props.model;
  return React.createElement("div", null, React.createElement(BaseLinearDisplayComponent, _objectSpread({}, props)), React.createElement(StatBars, {
    model: model
  }));
});
export { YScaleBar };